/* eslint-disable consistent-return */
/* eslint-disable import/no-extraneous-dependencies */
import React, {useEffect, useRef} from 'react'
import SockJS from 'sockjs-client'
import {Client} from '@stomp/stompjs'
import {getAccessToken} from 'api/storageApi'
import {getApiHost} from 'helpers/envHelper'
import useFailureModal from 'features/modal/useFailureModal'
import useSuccessModal from 'features/modal/useSuccessModal'
import {
  PAYMENT_FAILED,
  PAYMENT_SUCCESS,
} from 'pages/ProductOrderPages/constants'
import {useTranslation} from 'react-i18next'

interface StompClientProps {
  paymentId: string
  onPaymentStatusUpdate?: (status: any) => void
  onLoadingChange?: (isLoading: boolean) => void
}
interface PaymentStatus {
  paymentId: string
  status: string
  amount?: number
  timestamp?: string
  code: string
  data: string
  errorCode: string
  msg: string
}
const StompClient = ({
  paymentId,
  onPaymentStatusUpdate,
  onLoadingChange,
}: StompClientProps) => {
  const clientRef = useRef<Client | null>(null)
  const mountedRef = useRef(true)
  const {onOpen: onOpenFail} = useFailureModal()
  const {onOpen: onOpenSuccess} = useSuccessModal()
  const {t} = useTranslation()

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!paymentId) return

    const socket = new SockJS(
      `${getApiHost()}/shop/ws-stomp?Authorization=${getAccessToken()}`,
    )

    const client = new Client({
      webSocketFactory: () => socket,
      reconnectDelay: 5000,
      connectionTimeout: 10000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      onConnect: () => {
        if (!mountedRef.current) return

        client.publish({
          destination: `/pub/payment/status/${paymentId}`,
          body: JSON.stringify({paymentId}),
        })

        client.subscribe(`/sub/payment/status/${paymentId}`, (message) => {
          if (!mountedRef.current) return

          try {
            const paymentStatus: PaymentStatus = JSON.parse(message.body)

            if (!paymentStatus) return

            if (PAYMENT_SUCCESS.includes(paymentStatus.data)) {
              if (onPaymentStatusUpdate) {
                onLoadingChange?.(false)
                onPaymentStatusUpdate(paymentStatus)
              }
              onOpenSuccess(t('IPaymentSuccess'))
            } else if (PAYMENT_FAILED.includes(paymentStatus.data)) {
              if (onPaymentStatusUpdate) {
                onLoadingChange?.(false)
                onPaymentStatusUpdate(paymentStatus)
              }
              onOpenFail(t('IReasonPaymentFailed'))
            }
          } catch (error) {
            onLoadingChange?.(false)
            onOpenFail(t('IReasonPaymentFailed'))
          }
        })
      },

      onStompError: () => {
        if (!mountedRef.current) {
          onLoadingChange?.(false)
          onOpenFail(t('IReasonPaymentFailed'))
        }
      },

      onWebSocketClose: (event) => {
        if (!mountedRef.current) {
          onLoadingChange?.(false)
          if (event.code !== 1000) {
            onOpenFail(t('IReasonPaymentFailed'))
          }
        }
      },

      onDisconnect: () => {
        if (!mountedRef.current) {
          onLoadingChange?.(false)
        }
      },
    })

    client.activate()
    clientRef.current = client

    return () => {
      if (client) {
        client.deactivate()
      }
      clientRef.current = null
    }
  }, [paymentId])

  return <div />
}
export default StompClient
